import type { FC, PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";
import {
    type IntersectionOptions,
    useInView
} from "react-intersection-observer";

export type Adg4TextUnderlineProps = PropsWithChildren<{
    /**
     * The text to be underlined. If not provided, children will be used
     */
    text?: string;
    /**
     * The underline svg
     */
    underline: ReactNode;
    /**
     * The inset of the underline from the bottom of the text
     * Using em is recommended to work across different font sizes
     * @default default -37.5%
     */
    underlineInset?: string;
    /**
     * Left align the underline not adding inline margin auto
     */
    underlineLeft?: boolean;
    /**
     * The font weight animation
     * @default { from: 400, to: 800 }
     */
    fontWeight?: {
        from: number;
        to: number;
    };
    /**
     * Custom options for the useInView hook
     */
    useInViewOptions?: IntersectionOptions;
}>;

const textStyle = css({
    "display": "inline-flex",
    "margin": "0",
    "position": "relative",
    "animationDelay": "0.5s",
    "animationDuration": "0.2s",
    "animationIterationCount": 1,
    "animationFillMode": "forwards",
    "lineHeight": 1,
    "wordBreak": "break-word",
    "@keyframes strokeright": {
        "0%": {
            clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)"
        },
        "100%": {
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
        }
    }
});

const inViewTextStyle = css({
    animationName: "boldin"
});

const underlineStyle = css({
    display: "flex",
    width: "100%",
    clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)",
    animationDelay: "0.5s",
    animationDuration: "0.7s",
    animationIterationCount: 1,
    insetInlineStart: 0,
    animationFillMode: "forwards",
    position: "absolute",
    zIndex: -1
});
const inViewUnderlineStyle = css({
    animationName: "strokeright"
});

const hiddenText = css({
    visibility: "hidden",
    pointerEvents: "none"
});

const visibleText = css({
    position: "absolute",
    insetInlineStart: 0
});

export const Adg4TextUnderline: FC<Adg4TextUnderlineProps> = ({
    text,
    children,
    underline,
    underlineInset = "-37.5%",
    underlineLeft = false,
    fontWeight = {
        from: 400,
        to: 800
    },
    useInViewOptions
}: Adg4TextUnderlineProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } =
        useInView(useInViewOptions);

    const fontWeightAnimation = css({
        "@keyframes boldin": {
            "0%": {
                fontWeight: fontWeight.from
            },
            "100%": {
                fontWeight: fontWeight.to
            }
        }
    });

    const underlineInsetStyle = css({
        insetBlockEnd: underlineInset,
        svg: {
            marginInlineStart: underlineLeft ? "0" : "auto",
            marginInlineEnd: underlineLeft ? "0" : "auto"
        }
    });

    const toFontWeight = css({
        fontWeight: fontWeight.to
    });

    return (
        <span
            ref={scrollRef}
            css={[
                textStyle,
                fontWeightAnimation,
                sectionIsVisible && inViewTextStyle
            ]}
        >
            <span css={[hiddenText, toFontWeight]} aria-hidden>
                {text ?? children}
            </span>
            <span css={visibleText}>{text ?? children}</span>
            <div
                css={[
                    underlineStyle,
                    underlineInsetStyle,
                    sectionIsVisible && inViewUnderlineStyle
                ]}
            >
                {underline}
            </div>
        </span>
    );
};
